import { subscribe as sub, publish, unsubscribe } from "../messageService";

const dataStore = new Map(); // key -> Data
const pendingMap = new Map(); // key -> Promise
const controllerMap = new Map(); // key -> AbortController


/**
 * Sort object or array recursively
 * @param {Object} obj object or array to sort
 * @erturns {Object} sorted object or array
 */
function sortObjectDeep(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(sortObjectDeep);
  }

  const sortedKeys = Object.keys(obj).sort();
  const sortedObj = {};

  for (const key of sortedKeys) {
    sortedObj[key] = sortObjectDeep(obj[key]);
  }

  return sortedObj;
}

/**
 * KWC cache key generator
 * @param {Object} config Wire Adapter config
 * @returns {String} cache key
 */
function getCacheKey(config) {
  if (!config || typeof config !== 'object') {
    return String(config);
  }

  const normalizedConfig = sortObjectDeep(config);
  return JSON.stringify(normalizedConfig);
}

/**
 * Subscribe to data changes
 * @param {Object} config adapter config
 * @param {Function} subscriber callback to be called when data changes
 * @returns {Function} unsubscribe function
 */
export function subscribe(config, subscriber) {
  const key = getCacheKey(config);
  if (dataStore.has(key)) {
    subscriber(dataStore.get(key));
  }

  const component = {}
  sub(component, key, subscriber)
  return () => {
    unsubscribe(component, key, subscriber);
  }
}

/**
 * Fetch data with cache
 * @param {Object} config configuration to generate cache key and url
 * @param {Function} dataFetcher function to fetch data
 * @param {Object} options fetch options
 * @param {Number} options.ttlMs cache ttl in milliseconds
 * @param {Boolean} options.force force fetch refresh
 * @param {Boolean} options.dedupe dedupe requests
 * @param {Boolean} options.abortPrevious abort previous request
 * @returns {Promise} promise that resolves to data or error
 */
export async function fetchDataWithCache(config, dataFetcher, options = {}) {
  const { ttlMs = 60000, force = false, dedupe = true, abortPrevious = true } = options;
  const key = getCacheKey(config);
  const cached = dataStore.get(key);
  const nowTs = Date.now();

  // Return cached if not expired and not force
  if (cached && !force) {
    const ttl = cached.ttl ?? ttlMs;
    if (!cached.ts || (nowTs - cached.ts) <= ttl) {
      return cached;
    }
  }

  // Concurrency dedupe: resuse in-flight promise if desired
  if (dedupe && pendingMap.has(key) && !force) {
    return pendingMap.get(key);
  }

  // Abort previous in-flight for the same key if requested
  if (abortPrevious && controllerMap.has(key)) {
    try {
      controllerMap.get(key).abort();
    } catch (error) {
      controllerMap.delete(key);
      pendingMap.delete(key);
      console.error('Failed to abort previous request', error);
    }
  }

  const controller = new AbortController();
  controllerMap.set(key, controller);

  const p = (async () => {
    try {
      const res = await dataFetcher(config, { signal: controller.signal });
      let payload = null;

      try {
        payload = await res.json();
      } catch {
        payload = null;
      }

      if (!res.ok) {
        const result = {
          data: null,
          error: { status: res.status, message: (payload && payload.message) || 'Request failed' },
          ts: nowTs,
          ttl: ttlMs
        };
        dataStore.set(key, result);
        publish(key, result);
        return result;
      }

      const result = { data: payload, error: null, ts: nowTs, ttl: ttlMs };
      dataStore.set(key, result);
      publish(key, result);
      return result;
    } catch (error) {
      if (error?.name === 'AbortError') {
        return { data: null, error: { status: 'ABORTED', message: error.message }, aborted: true }; 
      }

      const result = { data: null, error: { status: 0, message: error?.message || 'Network error' }, ts: nowTs, ttl: ttlMs };
      dataStore.set(key, result);
      publish(key, result);
      return result;
    } finally {
      controllerMap.delete(key);
      pendingMap.delete(key);
    }
  })();

  pendingMap.set(key, p);
  return p;
}

export async function invalidateCache(config, dataFetcher) {
  const key = getCacheKey(config);

  if (dataStore.has(key)) {
    dataStore.delete(key);
  }

  if (controllerMap.has(key)) {
    try {
      controllerMap.get(key).abort();
    } catch {}
    controllerMap.delete(key);
  }

  pendingMap.delete(key);
  await fetchDataWithCache(config, dataFetcher, { force: true, dedupe: false, abortPrevious: false });
}

export function updateCache(config, data, options = {}) {
  const { ttlMs = 60000 } = options;
  const key = getCacheKey(config);
  const result = { data, error: null, ts: Date.now(), ttl: ttlMs };
  dataStore.set(key, result);
  publish(key, result);
}